import { showNotification } from '../../../_shared/views/Notifications';
import { handleError } from '../../../services/error-notification';
import {
  NotificationsListModel,
  createSetNotificationDismissed,
  createSetNotificationSeenUnsync,
  createSetNotificationsSeen,
  initialNotificationsListModel,
  updateNotificationList,
} from './models/notifications-list';
import { NotificationItem, NotificationsApi } from './notifications-api';
import { NotificationListControlDelegate } from './views/NotificationsListControl';

export class NotificationController implements NotificationListControlDelegate {
  private orgApi = new NotificationsApi();

  private model = initialNotificationsListModel();

  private unsubscribeGetNotifications: (() => void) | undefined;

  private notificationCallbacks: ((notification: NotificationItem) => Promise<void>)[] = [];

  constructor(private updateViewState: (_: NotificationsListModel) => void) {
    this.start();
  }

  private update = (model: NotificationsListModel) => {
    const existingModel = {
      loading: this.model.loading,
      notifications: [...this.model.notifications],
    };
    this.model = model;
    this.updateViewState(model);
    this.triggerNotifications(existingModel, model);
  };

  private start() {
    if (!this.unsubscribeGetNotifications) {
      this.unsubscribeGetNotifications = this.orgApi.initNotificationsInterval((response) => {
        this.update(updateNotificationList(response));
      });
    }
  }

  dispose() {
    this.unsubscribeGetNotifications?.();
  }

  async onSetNotificationDismissed(notificationId?: number): Promise<void> {
    const newModel = await createSetNotificationDismissed(this.model, this.orgApi, handleError, notificationId ?? undefined);
    this.update(newModel);
  }

  async onSetNotificationSeenUnsync(notificationId: number): Promise<void> {
    const newModel = await createSetNotificationSeenUnsync(this.model, notificationId);
    this.update(newModel);
  }

  async onSetNotificationsSeen(): Promise<void> {
    const newModel = await createSetNotificationsSeen(this.model, this.orgApi, handleError);
    this.update(newModel);
  }

  private triggerNotifications(existingModel: NotificationsListModel, newModel: NotificationsListModel) {
    if (existingModel.loading && !newModel.loading) {
      return;
    }

    const ids = existingModel.notifications.map(({ id }) => id);
    const newAlerts = newModel.notifications.filter(({ id }) => !ids.includes(id));
    if (newAlerts.length === 0) {
      return;
    }

    for (const alert of newAlerts) {
      const onClick = alert.referencedEntity
        ? (event?: MouseEvent) => {
            event?.stopPropagation();
            event?.preventDefault();

            void this.onNotificationClicked?.(alert.id);
          }
        : undefined;

      showNotification({
        type: 'info',
        key: `notification_${alert.id}`,
        message: alert.title,
        description: alert.description,
        onClick,
      });
    }
  }

  async onNotificationClicked(notificationId: number): Promise<void> {
    await this.onSetNotificationSeenUnsync(notificationId);
    await this.onSetNotificationsSeen();
    const notification = this.model.notifications.find(({ id }) => id === notificationId);
    if (!notification || this.notificationCallbacks.length === 0) {
      return;
    }

    await this.notificationCallbacks[0](notification);
  }

  onNotificationInvoked(notificationHandler: ((notification: NotificationItem) => Promise<void>) | undefined): void {
    this.notificationCallbacks.length = 0;

    if (notificationHandler) {
      this.notificationCallbacks.push(notificationHandler);
    }
  }
}
